<template>
    <div class="container" v-if="isDataReady">
        <v-title :breadcrumb="breadcrumb">
            Order for {{orderFor}}
        </v-title>
        <div class="grid" v-if="isDataReady">
            <div class="grid-item grid-item-primary">
                <div class="grid">
                    <div class="grid-item grid-item-full">
                        <order-summary :order="order" :summary="summary">
                            <template #content="{ orderDetails }">
                                <div class="tile-content">
                                    <div class="grid-content">
                                        <div class="grid-item stack-m">
                                            <h2>Order Details</h2>
                                            <span class="text-muted">{{ orderDetails.order.id | truncate({dots: true, limit: 13}) }}</span>
                                        </div>
                                        <div class="grid-item no-flex align-right">
                                            <span class="text-muted" v-fs-block>Placed on {{ orderDetails.order.startTime |  dateTime }}<br>from {{orderDetails.order.websiteId}}</span>
                                        </div>
                                    </div>
                                    <div>
                                        <r-badge class="inline-s" :type="status.type" v-for="(status, statusIndex) in orderDetails.statusBadges" :key="statusIndex" v-fs-block>{{status.value | capitalize}}</r-badge>
                                    </div>
                                </div>
                                <div class="tile-content position-relative">
                                    <div class="inset-m" v-if="!orderDetails.summary" v-fs-block>
                                        <v-inline-loader/>
                                    </div>
                                    <div v-else class="stat-group stat-group-fluid" v-fs-block>
                                        <div class="stat" v-for="(metric, index) in orderDetails.summaryMetrics" :key="index">
                                            <div class="stat-title">{{metric.label}}</div>
                                            <div class="stat-value" v-fs-block>{{metric.value}}</div>
                                        </div>
                                    </div>
                                </div>
                                <div class="tile-content">
                                    <div class="grid-content" v-fs-block>
                                        <div class="grid-item" v-if="orderDetails.canCancelAndUpdateRenewal && !order.isPending">
                                            <cancel-reactivate-order :order="order"/>
                                        </div>
                                        <template v-if="!orderDetails.order.isCanceled">
                                            <div class="grid-item"
                                                 :class="{'grid-item-third': !orderDetails.canCancelAndUpdateRenewal}">
                                                <redeem-coupon :order="order"/>
                                            </div>
                                            <div v-if="orderDetails.canCancelAndUpdateRenewal" class="grid-item">
                                                <update-renewal :order="orderDetails.order"/>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                            </template>
                        </order-summary>
                    </div>
                    <div class="grid-item grid-item-full">
                        <div class="grid-item grid-item-full">
                            <div class="tile position-relative">
                                <div class="tile-title">
                                    <slot name="title">
                                        <h2>Invoices</h2>
                                    </slot>
                                </div>
                                <invoices
                                    v-if="$auth.invoices.edit.isAllowed"
                                    :invoices-params="{
                                        sort: `-issuedTime`,
                                        filter: `subscriptionId:${order.id}`,
                                        expand: 'organization,items.*.product,items.*.plan',
                                    }"
                                    :key="`order-${order.id}-invoices`">
                                    <template slot="no-invoices">
                                        <p class="text-muted">This order has no invoices</p>
                                    </template>
                                </invoices>
                            </div>
                        </div>
                    </div>
                    <div class="grid-item grid-item-full">
                        <timeline
                            ref="timeline"
                            type="order"
                            :key="`order-${order.id}-timeline`"
                            :entity="order"/>
                    </div>
                </div>
            </div>
            <div class="grid-item grid-item-secondary">
                <div class="grid">
                    <div class="grid-item grid-item-full">
                        <compact-customer-summary
                            :entry="order.customer"
                            :expand="order"
                            :key="`order-${order.id}-${order.customer.id}-customer-summary`">
                            <template slot="additional-information">
                                <div class="tile-content">
                                    <contact-manager
                                        label="Billing Address"
                                        email-actions
                                        @email-sent="$refs.timeline.update()"
                                        :editable="canEditContacts"
                                        :customer-id="order.customer.id"
                                        :address="order.billingAddress">
                                        <template slot="list-action" slot-scope="{contact, isActive}">
                                            <r-button
                                                v-if="!isActive"
                                                @click="setBillingAddress(contact)"
                                                size="small">
                                                Set as Billing Address
                                            </r-button>
                                        </template>
                                    </contact-manager>
                                </div>
                                <div class="tile-content">
                                    <contact-manager
                                        label="Delivery Address"
                                        email-actions
                                        :editable="canEditContacts"
                                        :customer-id="order.customer.id"
                                        :address="order.deliveryAddress">
                                        <template slot="list-action" slot-scope="{contact, isActive}">
                                            <r-button
                                                v-if="!isActive"
                                                @click="setDeliveryAddress(contact)"
                                                size="small">
                                                Set as Delivery Address
                                            </r-button>
                                        </template>
                                    </contact-manager>
                                </div>
                            </template>
                        </compact-customer-summary>
                    </div>
                    <div class="grid-item grid-item-full">
                        <risk-analysis :model="order" :key="`order-${order.id}-risk-analysis`"/>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import vTitle from '@/components/v-title';
    import vInlineLoader from '@/components/v-inline-loader';
    import CancelReactivateOrder from '@/modules/orders/components/order-summary/cancel-reactivate-order';
    import RedeemCoupon from '@/modules/orders/components/order-summary/redeem-coupon';
    import UpdateRenewal from '@/modules/orders/components/order-summary/update-renewal';
    import FormLoaderMixin from '@/mixins/form-loader-mixin';
    import OrderModel from '@/common/models/order-model';
    import OrderSummaryModel from '@/common/models/order-summary-model';
    import EmailMessageModel from '@/common/models/email-message-model';
    import CompactCustomerSummary from '@/components/compact-customer-summary/compact-customer-summary';
    import Invoices from '@/modules/invoices/invoices-list';
    import RiskAnalysis from '@/components/risk-analysis/risk-analysis';
    import ContactManager from '@/components/contact-manager/contact-manager';

    const OrderSummary = () => import('@/modules/orders/components/order-summary');
    const Timeline = () => import('@/components/timeline/timeline.vue');

    export default {
        name: 'order-details',
        mixins: [FormLoaderMixin],
        provide() {
            return {
                // used by modals
                reactivate: this.reactivate,
                cancel: this.cancel,
                sendEmailToCustomer: this.sendEmailToCustomer,
                setRenewalTime: this.setRenewalTime,
            };
        },
        components: {
            vTitle,
            vInlineLoader,
            CancelReactivateOrder,
            RedeemCoupon,
            UpdateRenewal,
            CompactCustomerSummary,
            OrderSummary,
            Timeline,
            RiskAnalysis,
            Invoices,
            ContactManager,
        },
        props: {
            id: String,
        },
        data() {
            return {
                // TODO determine if an order with no `cancelTime` and no `renewalTime` can be canceled or not
                order: new OrderModel(),
                summary: null,
                reloadOnParam: 'id',
            };
        },
        computed: {
            orderFor() {
                return this.order.billingAddress && this.order.billingAddress.organization
                    ? this.order.billingAddress.organization
                    : this.order.customer.displayName;
            },
            canEditContacts() {
                // future feature
                return false;
            },
            breadcrumb() {
                return {
                    label: 'Order Details',
                    route: {
                        name: 'order-details',
                        params: {
                            id: this.order.id,
                        },
                    },
                };
            },
        },
        methods: {
            async fetchData() {
                const subscriptionId = this.id;
                const subscriptionsParams = {
                    id: subscriptionId,
                    expand: 'customer',
                };
                try {
                    const [{fields: orderFields}, {fields: summaryFields}] = await Promise.all([
                        this.$api(({subscriptions}) => subscriptions.get(subscriptionsParams)),
                        (async () => {
                            try {
                                return await this.$api(({subscriptions}) => subscriptions.getSubscriptionSummaryMetrics({subscriptionId}));
                            } catch (error) {
                                this.summaryOrderInterval();
                                return {fields: null};
                            }
                        })(),
                    ]);
                    this.order = new OrderModel(orderFields);
                    if (summaryFields !== null) {
                        this.summary = new OrderSummaryModel(summaryFields);
                    }
                } catch (error) {
                    this.$router.push({name: 'orders'});
                    this.$toast.fromError(error, 'Unable to find the order you requested.');
                    throw error;
                }
            },
            summaryOrderInterval() {
                let counter = 0;
                const subscriptionId = this.id;
                const run = () => {
                    window.setTimeout(() => {
                        (async () => {
                            try {
                                const {fields} = await this.$api(({subscriptions}) => subscriptions.getSubscriptionSummaryMetrics({subscriptionId}));
                                this.summary = new OrderSummaryModel(fields);
                            } catch (e) {
                                counter += 1;
                                if (counter > 5) {
                                    this.summary = {};
                                    return;
                                }
                                run();
                            }
                        })();
                    }, 1000);
                };
                run();
            },
            /**
             * Toggles the order based on the delegate (cancellation or reactivation) and
             * handles it in a generic way by sending an email if required and displaying a
             * confirmation or error message once completed.
             * @param {Function} delegate
             * @param {boolean} sendEmail
             * @param {string} emailSubject
             * @param {string} emailText
             * @param {string} successMsg
             * @param {string} errorMsg
             * @return {Promise<void>}
             */
            async toggleOrder({
                delegate,
                sendEmail,
                emailSubject,
                emailText,
                successMsg,
                errorMsg,
            }) {
                try {
                    this.startLoadingPage();
                    const emailCustomer = () => {
                        if (sendEmail) {
                            this.sendEmailToCustomer({
                                subject: emailSubject,
                                text: emailText,
                            });
                        }
                    };
                    await delegate();
                    await Promise.all([
                        this.refresh(),
                        emailCustomer(),
                    ]);
                    this.$toast.positive(successMsg);
                } catch (error) {
                    this.stopLoadingPage();
                    this.$toast.negative(errorMsg);
                }
            },
            /**
             * Provided injection to a modal down the component chain that reactivates the
             * order and optionally sends an email to inform the customer.
             * @param {Object} data
             * @param {boolean} sendEmail
             * @return {Promise<void>}
             */
            async reactivate({data, sendEmail}) {
                await this.toggleOrder({
                    sendEmail,
                    delegate: () => this.$api(({subscriptionReactivations}) => subscriptionReactivations.reactivate({data})),
                    emailSubject: `Your order (#${this.order.id}) has been reactivated`,
                    emailText: `Dear ${this.order.customer.displayName},\n\nYour order (#${this.order.id}) has been reactivated.\n\nThank you`,
                    successMsg: 'Order has been reactivated successfully.',
                    errorMsg: 'Unable to reactivate this order.',
                });
            },
            /**
             * Provided injection to a modal down the component chain that cancels the
             * order and optionally sends an email to inform the customer.
             * @param {Object} data
             * @param {boolean} sendEmail
             * @return {Promise<void>}
             */
            async cancel({data, sendEmail}) {
                await this.toggleOrder({
                    sendEmail,
                    delegate: () => this.$api(({subscriptionCancellations}) => subscriptionCancellations.create({data})),
                    emailSubject: `Your order (#${this.order.id}) has been canceled`,
                    emailText: `Dear ${this.order.customer.displayName},\n\nYour order (#${this.order.id}) has been canceled.\n\nThank you`,
                    successMsg: 'Order has been canceled successfully.',
                    errorMsg: 'Unable to cancel this order.',
                });
            },
            /**
             * Refresh the local data to update all UI elements after a modification to the order.
             * or a reactivation.
             * @return {Promise<void>}
             */
            async refresh() {
                this.isDataReady = false;
                await this.loadComponent();
            },
            /**
             * Send an email to the customer to inform them of an action the merchant has taken
             * with this order.
             * @param {string} subject
             * @param {string} text
             */
            sendEmailToCustomer({subject, text}) {
                const email = new EmailMessageModel({
                    to: [this.order.customer.email],
                    from: this.$store.getters['user/email'],
                    subject,
                    text,
                    metadata: {
                        customerId: this.order.customer.id,
                        eventType: 'direct-email-sent',
                    },
                    attachments: [],
                });
                (async () => {
                    const data = email.getPayload();
                    try {
                        const message = await this.$api(({emailMessages}) => emailMessages.create({data}));
                        await this.$api(({emailMessages}) => emailMessages.send({id: message.fields.id}));
                    } catch (error) {
                        this.$toast.fromError(error, 'Unable to send the email. Please try again later or contact support.');
                    }
                })();
            },
            async updateOrder(data) {
                await this.$api(({subscriptions}) => subscriptions.update({
                    id: this.order.id,
                    data: {
                        ...this.order.getPayload(),
                        ...data,
                    },
                }));
                this.refresh();
            },
            /**
             * Update order RenewalTime
             * @param {string} renewalTime (utc)
             * @returns {Promise<void>}
             */
            async setRenewalTime(renewalTime) {
                try {
                    await this.updateOrder({
                        renewalTime,
                    });
                    this.$toast.positive(`Renewal Time successfully updated.`);
                } catch (e) {
                    this.$toast.fromError(e, `Unable to update Renewal Time, please contact support`);
                }
            },
            async setBillingAddress() {
                // await this.updateOrder({billingAddress: contact.getPayload()})
            },
            setDeliveryAddress() {
                // console.log('setDeliveryAddress', contact);
            },
        },
    };
</script>
